import './AssetCommand.scss';
import React, { useCallback, useState } from 'react';
import _ from 'lodash';
import { DateTime, Duration } from 'luxon';
import { Button, ButtonVariant, Spinner, Tooltip, TextInput, FormGroup } from '@patternfly/react-core';
import { Icons } from '../Common';
import { v4 as uuidv4 } from 'uuid';

import useApiCall from '../../services/useApiCall';
import useAPI from '../../services/useAPI';
import { useTranslation } from 'localization/useTranslation';

const CommandStatus = {
  READY: 'ready',
  COMPLETED: 'completed',
  PENDING: 'pending',
  RUNNING: 'running',
  CANCELLED: 'cancelled',
  QUEUED: 'queued',
  FAILED: 'failed'
};

const isPendingState = (state) => {
  const normalizedStateString = state.toLowerCase();
  return normalizedStateString === CommandStatus.PENDING ||
    normalizedStateString === CommandStatus.RUNNING ||
    normalizedStateString === CommandStatus.QUEUED;
};

const convertParameter = (parameterString, schema) => {
  switch (schema) {
    case 'string': return parameterString;
    case 'long':
    case 'integer': return _.parseInt(parameterString);
    case 'double':
    case 'float': return Number.parseFloat(parameterString);
    case 'boolean': return _.lowerCase(parameterString) === 'true';
    case 'time':
    case 'date':
    case 'dateTime': return DateTime.fromISO(parameterString);
    case 'duration': return Duration.fromISO(parameterString);
    default: console.error('Unsupported schema type for command parameter');
  }
};

const AssetCommand = ({ command, assetId }) => {
  const [commandStatus, setCommandStatus] = useState(CommandStatus.READY);
  // const [jobStatusInfo, setJobStatusInfo] = useState();
  const [parameterString, setParameterString] = useState('');
  const api = useAPI();
  const T = useTranslation();

  const [executeAssetCommand] = useApiCall({
    apiCall: api.core.executeAssetCommand,
    onSuccess: useCallback(() => {
      setCommandStatus(CommandStatus.COMPLETED);
    }, []),
    onError: useCallback(() => {
      setCommandStatus(CommandStatus.FAILED);
    }, [])
  });

  const onCommand = useCallback(() => {
    setCommandStatus(CommandStatus.PENDING);
    const msg = {
      commandName: (command.componentName) ? `${command.componentName}.${command.name}` : command.name,
      payload: (command.request) ? convertParameter(parameterString, command.request.schema) : null,
      correlationId: uuidv4()
    };
    executeAssetCommand(assetId, msg);
  }, [assetId, command.componentName, command.name, command.request, parameterString, executeAssetCommand]);


  const updateParameterFromInput = useCallback((value) => {
    setParameterString(value);
  }, []);

  return (
    <div className="command-container">
      { command.request &&
      <FormGroup
        className="text-input-wrapper"
        label={command.request.displayName || command.request.name}
        fieldId={`command-${command.name}-status-text`}
      >
        <TextInput
          id={`command-${command.name}-status-text`}
          value={parameterString}
          onChange={(_event, value) => updateParameterFromInput(value)}
          className="command-param-text text-input"
        />
      </FormGroup>
      }
      <Tooltip
        position="top"
        content={command?.description || T.common.noDescription}
        isVisible={command?.description}
        trigger={command?.description ? 'mouseenter focus' : 'manual'}
      >
        <Button
          variant={ButtonVariant.primary}
          onClick={onCommand}
          disabled={isPendingState(commandStatus)}
          className="command-button"
        >
          { command.displayName || command.name }
        </Button>
      </Tooltip>
      { commandStatus === CommandStatus.FAILED &&
        <Tooltip
          position="top"
          className="command-icon-tooltip-container command-error"
          // tooltipText={`Failed to execute command${jobStatusInfo ? ` (${jobStatusInfo?.error?.code} - ${jobStatusInfo?.error.description})` : ''}`}
        >
          <Icons.Warning className="command-icon"/>
        </Tooltip>
      }
      { isPendingState(commandStatus) &&
        <Tooltip
          position="top"
          className="command-icon-tooltip-container"
          content="Executing command..."
        >
          <Spinner size="md"/>
        </Tooltip>
      }
      { commandStatus === CommandStatus.COMPLETED &&
        <Tooltip
          position="top"
          className="command-icon-tooltip-container command-success"
          content="Command executed"
        >
          <Icons.Checkmark />
        </Tooltip>
      }
    </div>
  );
};

export default AssetCommand;
